/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import styled from 'styled-components';
import {
  Button,
  Card,
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import Swal from 'sweetalert2';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import ReactTooltip from 'react-tooltip';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useTable } from 'react-table';

import SimpleHeader from 'components/Headers/SimpleHeader.js';

import { getGroupList, createGroup, updateGroupOrder, editGroup, deleteMemberGroup } from 'apis/admin/userApi';

// styling --------------------------------------------------\\
const Styles = styled.div`
  table {
    width: 100%;
    border-spacing: 0;
    text-align: center;
    font-size: 14px;
    border: 1px solid rgba(0, 0, 0, 0.05);

    thead tr th {
      font-size: 13px;
      padding: 18px 8px;
      background-color: #f8f9fe;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    thead tr th:nth-child(1) {
      max-width: 150px;
    }

    tbody tr:hover {
      background-color: #f8f9fe;
    }

    tbody tr:nth-child(1) td:nth-child(2) {
      /* color: #5e72e4; */
      font-weight: 700;
    }

    th,
    td {
      margin: 0;
      padding: 0.7rem 0.3rem;
    }

    td {
      input {
        font-size: 1rem;
        padding: 0;
        margin: 0;
        border: 0;
      }
    }
  }
`;

const Tr = styled.tr`
  display: ${({ isDragging }) => (isDragging ? 'table' : '')};
  opacity: ${({ isDragging }) => (isDragging ? 0.55 : 1)};
  background-color: ${({ isDragging }) => (isDragging ? '#f8f9fe' : '#ffffff')};
`;
// styling --------------------------------------------------//

function GroupTable({ columns, data, reorderData }) {
  const table = useTable({
    columns,
    data,
    reorderData,
  });

  const { getTableProps, headerGroups, prepareRow, rows } = table;

  const handleDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    reorderData(source.index, destination.index);
  };

  return (
    <>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
              ))}
            </tr>
          ))}
        </thead>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="table-body">
            {(provided, snapshot) => (
              <tbody ref={provided.innerRef} {...provided.droppableProps}>
                {rows.map((row, i) => {
                  prepareRow(row);
                  return (
                    <Draggable
                      draggableId={String(row.original.id)}
                      key={row.original.id + String(row.values.id)}
                      index={row.index}
                    >
                      {(provided, snapshot) => {
                        return (
                          <Tr
                            {...row.getRowProps()}
                            {...provided.draggableProps}
                            ref={provided.innerRef}
                            isDragging={snapshot.isDragging}
                          >
                            {row.cells.map((cell) => (
                              <td {...cell.getCellProps()}>
                                {cell.render('Cell', {
                                  dragHandleProps: provided.dragHandleProps,
                                  isSomethingDragging: snapshot.isDraggingOver,
                                })}
                              </td>
                            ))}
                          </Tr>
                        );
                      }}
                    </Draggable>
                  );
                })}
              </tbody>
            )}
          </Droppable>
        </DragDropContext>
      </table>
    </>
  );
}

function GroupList() {
  const user = useSelector((state) => state.user);
  
  const [show, setShow] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [groupList, setGroupList] = useState([]);
  const [groupToEdit, setGroupToEdit] = useState({});

  const { register, handleSubmit } = useForm();

  const handleOpenModal = () => setShow(true);
  const handleCloseModal = () => setShow(false);

  // 기존 그룹 목록 불러오기
  const getMemberGroupList = async () => {
    await getGroupList()
      .then((response) => {
        if (response.data.success) {
          setGroupList(response.data.result);
        }
      })
      .catch((error) => {
        console.error(
          'error occured in MemberGroups.js - getMemberGroupList() ',
          error
        );

        Swal.fire({
          icon: 'error',
          title: '그룹 목록을 불러올 수 없습니다.',
          text: '잠시 후 다시 시도해주세요.',
        }).then(() => {
          return;
        });
      });
  };

  // 그룹 순서 재배치
  const updateGroupListOrder = async (newData) => {
    const tempArr = [];
    for (let i = 0; i < newData.length; i++) {
      let tempObj = {...newData[i]};
      tempObj.order = i + 1;
      tempArr.push(tempObj);
    }
    
    await updateGroupOrder(tempArr)
    .then((response) => {
      if (response.data.success) {
        // window.location.reload();
        // setGroupList(tempArr);
        }
      })
      .catch((error) => {
        console.error(
          'error occured in MemberGroups.js - updateGroupListOrder() ',
          error
        );

        Swal.fire({
          icon: 'error',
          title: '그룹 순서를 편집할 수 없습니다.',
          text: '잠시 후 다시 시도해주세요.',
        }).then(() => {
          return;
        });
      });
  };

  // 그룹 추가
  const onSubmit = async (data) => {
    await createGroup(data)
      .then((response) => {
        if (!response.data.success) {
          Swal.fire({
            title: '그룹을 추가할 수 없습니다.',
            text: `${response.data.message}`,
            icon: 'warning',
          });
        } else {
          Swal.fire({
            title: '그룹을 추가 완료 하였습니다.',
            icon: 'success',
          }).then(() => {
            // window.location.replace('/admin/members/group');
            // props.history.push('/admin/members/group');
            getMemberGroupList();
          });
        }
      })
      .catch((error) => {
        console.error(
          'error occured in CreateMember.js - onSubmit(payload) ',
          error
        );
        Swal.fire({
          title: '그룹을 추가할 수 없습니다.',
          text: '잠시 후 다시 시도해주세요.',
          icon: 'error',
        }).then(() => {
          return;
        });
      });
  };

  useEffect(() => {
    getMemberGroupList();
  }, []);

  // 그룹 배치에 쓰이는 함수
  const reorderData = async (startIndex, endIndex) => {
    const newData = [...groupList];
    const [movedRow] = newData.splice(startIndex, 1);
    newData.splice(endIndex, 0, movedRow);
    setGroupList(newData);
    
    await updateGroupListOrder(newData);
  };

  // 그룹 순서 재배치 스타일링
  const StyledReoreder = (props) => (
    <span
      {...props.dragHandleProps}
      aria-label="move"
      className={props.className}
      role="img"
    >
      <i className="fas fa-bars" />
    </span>
  );

  // 모달창 닫기
  const handleCloseEditor = () => {
    setGroupToEdit({});
    setShowEditor(false);
  };

  // 그룹 수정 아이콘 클릭 시 동작되는 함수(모달창으로 해당 그룹 정보 넘기기)
  const setGroupInfoToEdit = (id, title, description, mem_count) => {
    if (title || description) {
      setGroupToEdit({ id, title, description, mem_count });
    }
    setShowEditor(true);
  };

  // 그룹 수정
  const handleGroupEdit = async (event) => {
    event.preventDefault();

    if (groupToEdit.title === '') {
      return Swal.fire({
        title: '그룹명은 필수 입력 사항입니다.',
        icon: 'warning',
      });
    }

    await editGroup(groupToEdit)
      .then((response) => {
        if (!response.data.success) {
          Swal.fire({
            title: '그룹을 수정할 수 없습니다.',
            text: `${response.data.message}`,
            icon: 'warning',
          });
        } else {
          Swal.fire({
            title: '그룹 수정을 완료 하였습니다.',
            icon: 'success',
          }).then(() => {
            setShowEditor(false);
            // window.location.reload();
            getMemberGroupList();
          });
        }
      })
      .catch((error) => {
        console.error(
          'error occured in CreateMember.js - handleGroupEdit(payload) ',
          error
        );
        Swal.fire({
          title: '그룹을 수정할 수 없습니다.',
          text: '잠시 후 다시 시도해주세요.',
          icon: 'error',
        }).then(() => {
          return;
        });
      });

    handleCloseEditor();
  };

  // 그룹 삭제
  const handleDeleteGroup = async (groupId) => {
    Swal.fire({
      title: '해당 그룹을 삭제 하시겠습니까?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '그룹 삭제',
      cancelButtonText: '취소',
      confirmButtonColor: '#f5365c',
    }).then(async (value) => {
      if (value.isConfirmed) {
        await deleteMemberGroup(groupId)
          .then(() => {
            setShowEditor(false);
            // window.location.reload();
            getMemberGroupList();
          })
          .catch((error) => {
            console.error(
              'error occured in CreateMember.js - handleDeleteGroup(payload) ',
              error
            );
            Swal.fire({
              title: '그룹을 삭제할 수 없습니다.',
              text: '잠시 후 다시 시도해주세요.',
              icon: 'error',
            }).then(() => {
              return;
            });
          });
      } else {
        Swal.close();
      }
    });
  };

  // 테이블 컬럼 정의
  const columns = useMemo(
    () => [
      {
        Header: () => (
          <span className="text-muted">
            순서{' '}
            <i
              className="fas fa-question-circle"
              data-tip="마우스를 드래그하여 순서를 변경할 수 있습니다."
              data-effect="solid"
            />
            <ReactTooltip />
          </span>
        ),
        accessor: '',
        id: 'editOrder',
        Cell: (props) => <StyledReoreder {...props} />,
      },
      {
        Header: () => (
          <span className="text-muted">
            그룹명{' '}
            <i
              className="fas fa-question-circle"
              data-tip="가장 위쪽에 위치한 그룹은 기본 값으로 설정됩니다."
              data-effect="solid"
            />
            <ReactTooltip />
          </span>
        ),
        accessor: 'title',
        id: 'title',
      },
      {
        Header: <span className="text-muted">그룹 설명</span>,
        accessor: 'description',
        id: 'description',
        width: 200,
      },
      {
        Header: <span className="text-muted">회원수</span>,
        accessor: 'mem_count',
        id: 'mem_count',
      },
      {
        Header: <span className="text-muted">그룹 생성 날짜</span>,
        accessor: 'createdAt',
        id: 'createdAt',
        Cell: ({ row }) => (
          row.original.createdAt &&
          moment(row.original.createdAt).format('YYYY[-]MM[-]DD HH[:]mm')
        ),
      },
      {
        Header: <span className="text-muted">그룹 수정</span>,
        id: 'editInfo',
        disableSortBy: true,
        Cell: ({ row }) => (
          <span
            style={{ cursor: 'pointer' }}
            onClick={() =>
              setGroupInfoToEdit(
                row.original.id,
                row.original.title,
                row.original.description,
                row.original.mem_count
              )
            }
          >
            <i className="fas fa-edit text-primary" />
          </span>
        ),
      },
    ],
    []
  );

  return (
    <>
      <SimpleHeader name="그룹 관리" parentName="회원 설정" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <div className="py-4 table-responsive">
                <Container fluid className="mb-3">
                  <Row>
                    <Col xs={12} sm={6} style={{ visibility: 'hidden' }}>
                      <div
                        id="datatable-basic_filter"
                        className="dataTables_filter"
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      {user && user.userData && user.userData.isAdmin && (
                        <div className="px-2 pb-1 float-right">
                          <>
                            <Button
                              className="buttons-copy buttons-html5"
                              color="primary"
                              size="sm"
                              onClick={handleOpenModal}
                            >
                              <span>그룹 추가</span>
                            </Button>
                          </>
                        </div>
                      )}
                    </Col>
                  </Row>
                </Container>
                <Styles>
                  <GroupTable
                    columns={columns}
                    data={groupList}
                    reorderData={reorderData}
                  />
                </Styles>
              </div>
            </Card>
          </div>
        </Row>
      </Container>
      <Modal
        isOpen={show}
        toggle={() => setShow(!show)}
        centered
        backdrop="static"
        fade={false}
      >
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader className="pb-1">
            <span className="ml-3 mt-2 text-lg">그룹 추가</span>
          </ModalHeader>
          <ModalBody>
            <Container className="mt-3 mb--3">
              <Row>
                <div className="col">
                  <FormGroup className="align-items-center">
                    <Label sm={4} className="text-sm">
                      그룹명
                    </Label>
                    <Col sm={12}>
                      <Input
                        type="text"
                        {...register('title', { required: true })}
                      />
                    </Col>
                  </FormGroup>
                  <FormGroup className="align-items-center">
                    <Label sm={4} className="text-sm">
                      그룹 설명
                    </Label>
                    <Col sm={12}>
                      <Input
                        type="textarea"
                        rows={3}
                        {...register('description')}
                      />
                    </Col>
                  </FormGroup>
                </div>
              </Row>
            </Container>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" type="submit" onClick={handleCloseModal}>
              그룹 추가
            </Button>{' '}
            <Button color="secondary" onClick={handleCloseModal}>
              취소
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
      <Modal
        isOpen={showEditor}
        toggle={() => setShowEditor(!showEditor)}
        centered
        backdrop="static"
        fade={false}
      >
        <Form onSubmit={handleGroupEdit}>
          <ModalHeader className="pb-1">
            <div className="ml-3 mt-2 text-lg">그룹 편집</div>
          </ModalHeader>
          <ModalBody>
            <Container className="mt-3 mb--3">
              <Row>
                <div className="col">
                  <FormGroup className="align-items-center">
                    <Label sm={4} className="text-sm">
                      그룹명
                    </Label>
                    <Col sm={12}>
                      <Input
                        type="text"
                        defaultValue={groupToEdit && groupToEdit.title}
                        onChange={(event) => {
                          const val = event.target.value;
                          setGroupToEdit((prev) => ({
                            ...prev,
                            title: val,
                          }));
                        }}
                      />
                    </Col>
                  </FormGroup>
                  <FormGroup className="align-items-center">
                    <Label sm={4} className="text-sm">
                      그룹 설명
                    </Label>
                    <Col sm={12}>
                      <Input
                        type="textarea"
                        rows={3}
                        defaultValue={groupToEdit && groupToEdit.description}
                        onChange={(event) => {
                          const val = event.target.value;
                          setGroupToEdit((prev) => ({
                            ...prev,
                            description: val,
                          }));
                        }}
                      />
                    </Col>
                  </FormGroup>
                </div>
              </Row>
            </Container>
          </ModalBody>
          <ModalFooter>
            {groupToEdit.mem_count > 0 && (
              <i
                className="fas fa-question-circle"
                data-tip="회원수가 한 명 이상이면 삭제할 수 없습니다."
                data-effect="solid"
              />
            )}
            <ReactTooltip />
            <Button
              color="danger"
              onClick={() => handleDeleteGroup(groupToEdit.id)}
              disabled={groupToEdit.mem_count > 0}
            >
              그룹 삭제
            </Button>{' '}
            <Button color="primary" type="submit" onClick={handleGroupEdit}>
              그룹 편집
            </Button>{' '}
            <Button color="secondary" onClick={handleCloseEditor}>
              취소
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
}

export default GroupList;
